/**
 * RivalryCoAdminsTab — iter87.6. Dedicated Rivalry-Admin tile that
 * shows who currently holds co-admin powers in this rivalry, plus a
 * quick reference to the permissions they carry.
 *
 * iter87.199.198 — Added an interactive "narrow it further" card,
 * primary-admin-only. Site Admin sets the global ceiling (max any
 * co-admin anywhere could have); this card lets THIS rivalry's admin
 * subtract from that ceiling for their own co-admin(s) — e.g. leave
 * everything off except Trophy Tokens. Can never grant beyond the
 * ceiling. Never visible to a co-admin themselves.
 *
 * Promotion/demotion itself is still a site-admin-only action (only
 * `sitePromoteCoAdmin` exists on the client).
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Crown, Shield, Mail, ClipboardList, Shuffle, Cable, Trophy, UserMinus, MessageSquare, Users, RefreshCw, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const TOGGLES = [
    { key: "can_configure_scoring", label: "Scoring", icon: ClipboardList },
    { key: "can_run_draft", label: "Draft", icon: Shuffle },
    { key: "can_manage_live_sync", label: "Live Sync", icon: Cable },
    { key: "can_configure_trophy_tokens", label: "Trophy Tokens", icon: Trophy },
    { key: "can_remove_participants", label: "Crewmates", icon: UserMinus },
    { key: "can_invite_players", label: "Invites", icon: Mail },
    { key: "can_post_messages", label: "Messages", icon: MessageSquare },
    { key: "can_manage_custom_tiles", label: "Teams/Schedule/Bracket (Custom)", icon: Users },
];

function CoAdminOverrideCard({ leagueId, noCoAdmins, isLocked }) {
    const [state, setState] = useState(null); // {ceiling, overrides, effective}
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.getCoAdminPermissionOverrides(leagueId);
            setState(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load co-admin overrides");
        }
        setLoading(false);
    }, [leagueId]);

    useEffect(() => { load(); }, [load]);

    const toggle = async (key, nextValue) => {
        setSavingKey(key);
        try {
            const data = await api.updateCoAdminPermissionOverrides(leagueId, { [key]: nextValue });
            setState(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setSavingKey(null);
    };

    const resetAll = async () => {
        if (!window.confirm("Reset — your co-admin(s) will inherit whatever the Site Admin's global toggles allow.")) return;
        setSavingKey("__reset__");
        try {
            const clear = Object.fromEntries(TOGGLES.map((t) => [t.key, null]));
            const data = await api.updateCoAdminPermissionOverrides(leagueId, clear);
            setState(data);
            toast.success("Reset — now inheriting Site Admin's global toggles");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reset failed");
        }
        setSavingKey(null);
    };

    if (loading || !state) {
        return (
            <div className="brutal-card p-5 text-center" data-testid="co-admin-override-loading">
                <RefreshCw className="w-5 h-5 mx-auto animate-spin text-zinc-500 mb-2" />
                <p className="text-xs text-zinc-500">Loading co-admin permissions for this rivalry…</p>
            </div>
        );
    }

    const anyNarrowed = Object.keys(state.overrides || {}).length > 0;

    return (
        <div
            className={`brutal-card p-5 space-y-3 border-cyber/40 ${noCoAdmins ? "opacity-50" : ""}`}
            data-testid="co-admin-override-card"
        >
            <div className="flex items-start justify-between gap-2">
                <div>
                    <p className="font-display font-extrabold text-base leading-tight">Co-Admin Permissions</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                        Select what your co-admin can do.
                    </p>
                </div>
                {anyNarrowed && !noCoAdmins && (
                    <button
                        type="button"
                        onClick={resetAll}
                        disabled={savingKey === "__reset__" || isLocked}
                        data-testid="co-admin-override-reset-btn"
                        className="brutal-btn-ghost text-[10px] py-1.5 px-2 shrink-0 whitespace-nowrap disabled:opacity-60"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Reset
                    </button>
                )}
            </div>

            <div className={`space-y-2 ${noCoAdmins ? "pointer-events-none" : ""} ${isLocked ? "opacity-50" : ""}`}>
                {TOGGLES.map((t) => {
                    const Icon = t.icon;
                    const ceilingOn = !!state.ceiling[t.key];
                    const on = !!state.effective[t.key];
                    const disabled = noCoAdmins || !ceilingOn || savingKey === t.key || isLocked;
                    const rowInteractive = ceilingOn && !noCoAdmins && !isLocked;
                    return (
                        <label
                            key={t.key}
                            data-testid={`co-admin-override-${t.key}`}
                            aria-disabled={disabled}
                            className={`brutal-card-light p-3 flex items-start gap-3 ${rowInteractive ? "cursor-pointer" : "opacity-70 cursor-not-allowed"} ${on ? "border-cyber/60" : "border-[#333]"}`}
                        >
                            <div className={`w-8 h-8 border-2 border-black shadow-brutal flex items-center justify-center shrink-0 ${on ? "bg-cyber" : "bg-zinc-800"}`}>
                                <Icon className={`w-4 h-4 ${on ? "text-black" : "text-zinc-500"}`} strokeWidth={2.5} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold leading-tight">{t.label}</p>
                                {!ceilingOn && (
                                    <p className="text-[10px] text-zinc-400 leading-relaxed mt-0.5">Site Admin has this off globally.</p>
                                )}
                            </div>
                            <input
                                type="checkbox"
                                data-testid={`co-admin-override-${t.key}-checkbox`}
                                checked={on}
                                disabled={disabled}
                                onChange={() => toggle(t.key, !on)}
                                className="w-5 h-5 accent-cyber cursor-pointer mt-1 shrink-0 disabled:cursor-not-allowed"
                            />
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

export default function RivalryCoAdminsTab({ league, data, me, isLocked }) {
    const participants = data?.participants || [];
    const owner = participants.find((p) => p.is_admin);
    const coAdmins = participants.filter((p) => p.is_co_admin && !p.is_admin);

    return (
        <div className="space-y-5" data-testid="rivalry-co-admins-tab">
            <div className="brutal-card-light p-3 flex items-start gap-3">
                <p className="text-xs text-zinc-300 leading-relaxed">
                    <strong className="text-cyber">Who else can run this rivalry.</strong> Co-admins share your admin
                    powers based on the permissions listed below. To add or remove a co-admin, message your site admin —
                    they can promote or demote from the Site Admin panel.
                </p>
            </div>

            <div className="brutal-card p-5 space-y-3" data-testid="rivalry-owner-card">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Rivalry owner</p>
                <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-cyber/20 border border-cyber flex items-center justify-center">
                        <Shield className="w-4 h-4 text-cyber" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="font-display font-extrabold text-base">
                            {owner?.username || "—"}
                            {owner?.id === me?.id && <span className="ml-2 text-[10px] uppercase text-cyber">(you)</span>}
                        </p>
                        <p className="text-[11px] text-zinc-500">Full admin — nothing is off-limits.</p>
                    </div>
                </div>
            </div>

            <div className="brutal-card p-5 space-y-3" data-testid="rivalry-co-admins-card">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Co-admins</p>
                    <span className="text-[10px] font-mono text-zinc-500" data-testid="co-admins-count">
                        {coAdmins.length} active
                    </span>
                </div>
                {coAdmins.length === 0 ? (
                    <p className="text-xs text-zinc-500 leading-relaxed">
                        No co-admins yet. Ask a site admin to promote a crewmate if you want backup running the rivalry.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {coAdmins.map((p) => (
                            <li
                                key={p.id}
                                data-testid={`co-admin-row-${p.id}`}
                                className="brutal-card-light p-3 flex items-center gap-3"
                            >
                                <span className="w-8 h-8 rounded-full bg-cyber/10 border border-cyber/40 flex items-center justify-center shrink-0">
                                    <Crown className="w-4 h-4 text-cyber" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-sm truncate">
                                        {p.username}
                                        {p.id === me?.id && <span className="ml-1.5 text-[10px] uppercase text-cyber">(you)</span>}
                                    </p>
                                    {p.crew_flair && (
                                        <p className="text-[10px] text-zinc-500 italic truncate">{p.crew_flair}</p>
                                    )}
                                </div>
                                {p.email && (
                                    <a
                                        href={`mailto:${p.email}`}
                                        className="brutal-btn-ghost text-[10px] py-1 px-2 shrink-0"
                                        title={`Email ${p.username}`}
                                    >
                                        <Mail className="w-3 h-3" />
                                    </a>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                <p className="text-[10px] text-zinc-600 leading-relaxed">
                    Exact permissions are controlled by the site admin (Tournament Admin + Rivalry Admin
                    toggles). Rivalry Settings, Co-Admins, and both Danger Zones always stay admin-only.
                </p>
            </div>

            {/* Primary admin only — a co-admin never sees or edits this. */}
            {me?.is_admin && (
                <CoAdminOverrideCard leagueId={league?.id} noCoAdmins={coAdmins.length === 0} isLocked={isLocked} />
            )}
        </div>
    );
}

